import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Instagram, Loader2, MapPin, MessageCircle } from "lucide-react";
import { PROFILES } from "../lib/profiles";
import { SITE, waLink } from "../lib/site";
import { FadeUp, Overline } from "./Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.25em] text-[#AEB9D0] mb-2">{label}</span>
    {children}
  </label>
);

const ContactForm = ({ defaultProfile }) => {
  const [form, setForm] = useState({ name: "", phone: "", city: "", profile: defaultProfile || "veiculos", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setDone(true);
      toast.success("Recebemos seu contato. Retornaremos em breve.");
    } catch {
      toast.error("Não foi possível enviar agora. Tente pelo WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="border border-[#D4AF37]/30 bg-[#111D3A] p-10">
        <p className="font-display text-3xl text-white">Obrigado, {form.name.split(" ")[0]}.</p>
        <p className="mt-4 text-[#C0C9DC]">Seu contato foi registrado. Se preferir agilizar, envie seus documentos pelo WhatsApp.</p>
        <a href={waLink()} target="_blank" rel="noreferrer" data-testid="contact-success-whatsapp" className="btn-gold mt-8">Continuar no WhatsApp</a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="grid gap-6 sm:grid-cols-2 border border-[#D4AF37]/20 bg-[#111D3A]/70 p-6 md:p-10">
      <Field label="Nome completo"><input required minLength={2} value={form.name} onChange={set("name")} data-testid="contact-name-input" className="ga-input" placeholder="Seu nome" /></Field>
      <Field label="WhatsApp"><input required minLength={8} value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" className="ga-input" placeholder="(00) 00000-0000" inputMode="tel" /></Field>
      <Field label="Cidade / UF"><input value={form.city} onChange={set("city")} data-testid="contact-city-input" className="ga-input" placeholder="Ex.: Rio de Janeiro / RJ" /></Field>
      <Field label="Sua situação">
        <select value={form.profile} onChange={set("profile")} data-testid="contact-profile-select" className="ga-input appearance-none">
          {PROFILES.map((p) => <option key={p.slug} value={p.slug}>{p.label}</option>)}
          <option value="outro">Outro</option>
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Conte brevemente o seu caso"><textarea rows={4} value={form.message} onChange={set("message")} data-testid="contact-message-input" className="ga-input resize-none" placeholder="Banco, tipo de bem, se já existe processo..." /></Field>
      </div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-xs text-[#8F9BB8] max-w-xs">Seus dados são usados apenas para retornarmos o contato.</p>
        <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="btn-gold justify-center disabled:opacity-60">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Solicitar análise <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </form>
  );
};

const Channel = ({ icon: Icon, label, value, href, testid }) => (
  <a href={href} target={href ? "_blank" : undefined} rel="noreferrer" data-testid={testid} className="group flex items-center gap-5 py-5 border-b border-[#D4AF37]/15">
    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/35 text-[#D4AF37] transition-[background-color,color] duration-500 group-hover:bg-[#D4AF37] group-hover:text-[#0B132B]"><Icon className="w-5 h-5" /></span>
    <span>
      <span className="block text-[11px] uppercase tracking-[0.25em] text-[#AEB9D0]">{label}</span>
      <span className="block mt-1 text-lg text-white">{value}</span>
    </span>
  </a>
);

export const Contact = ({ defaultProfile }) => (
  <section id="contato" data-testid="contact-section" className="relative mx-auto max-w-[1360px] px-6 lg:px-10 py-24 md:py-36">
    <div className="grid gap-14 lg:grid-cols-12">
      <FadeUp className="lg:col-span-5">
        <Overline>Contato</Overline>
        <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]">Fale com a gente <span className="text-gold-gradient italic">antes do prazo.</span></h2>
        <p className="mt-6 text-base md:text-lg text-[#C0C9DC] leading-relaxed max-w-md">Envie sua situação e receba uma análise inicial. Atendimento a partir do Rio de Janeiro, com possibilidade de atendimento remoto.</p>
        <div className="mt-10">
          <Channel icon={MessageCircle} label="WhatsApp" value={SITE.whatsappDisplay} href={waLink()} testid="contact-whatsapp-link" />
          <Channel icon={Instagram} label="Instagram" value={SITE.instagram} href={SITE.instagramUrl} testid="contact-instagram-link" />
          <Channel icon={MapPin} label="Endereço" value={SITE.city} testid="contact-address" />
        </div>
      </FadeUp>
      <FadeUp delay={0.1} className="lg:col-span-7">
        <ContactForm defaultProfile={defaultProfile} />
      </FadeUp>
    </div>
  </section>
);
